import { PROMPTS_CHECKPOINT_SIZE } from "../components/Modal/ModalImageEdit/hooks/imageEditCheckpoint";

export const BATCH_ITEM_STATUS = {
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
};

/**
 * Normalizes a single batch edit turn to the new contract shape.
 */
export const normalizeBatchPromptTurn = (turn) => {
  if (!turn || typeof turn !== "object") {
    return null;
  }

  const prompt = typeof turn.prompt === "string" ? turn.prompt : "";
  if (!prompt) return null;

  const normalized = {
    prompt,
    attachmentUrls: Array.isArray(turn.attachmentUrls)
      ? turn.attachmentUrls
      : [],
  };

  if (turn.resultUrls && typeof turn.resultUrls === "object") {
    normalized.resultUrls = { ...turn.resultUrls };
  }

  return normalized;
};

/**
 * Normalizes batch edit prompts from payload snapshot.
 */
export const normalizeBatchPrompts = (prompts) => {
  if (!Array.isArray(prompts)) {
    return [];
  }

  return prompts.map((turn) => normalizeBatchPromptTurn(turn)).filter(Boolean);
};

/**
 * Extracts batch result images from the new response contract only.
 */
export const getBatchResultImages = (response) => {
  if (!response?.ok || !response?.data) return [];

  if (response.data.kind === "images" && Array.isArray(response.data.images)) {
    return response.data.images
      .filter((image) => image?.selectionId && image?.url)
      .map((image) => ({
        selectionId: image.selectionId,
        url: image.url,
        rating: image.rating ?? null,
        ok: true,
      }));
  }

  return [];
};

/**
 * Converts normalized batch images into a selectionId -> url map.
 */
export const resultImagesToResultUrlsMap = (images = []) =>
  Object.fromEntries(
    images
      .filter((image) => image?.selectionId && image?.url)
      .map((image) => [image.selectionId, image.url])
  );

/**
 * Builds job payload turn objects for a new batch edit request.
 */
export const buildBatchPromptsForJob = ({
  history = [],
  prompt,
  attachmentUrls = [],
}) => {
  const safeHistory = normalizeBatchPrompts(history).map(
    ({
      prompt: turnPrompt,
      attachmentUrls: turnAttachmentUrls,
      resultUrls,
    }) => ({
      prompt: turnPrompt,
      attachmentUrls: turnAttachmentUrls,
      ...(resultUrls ? { resultUrls } : {}),
    })
  );

  return [
    ...safeHistory,
    {
      prompt,
      attachmentUrls: Array.isArray(attachmentUrls) ? attachmentUrls : [],
    },
  ];
};

/**
 * Resolves result URLs for a turn from snapshot or latest response.
 */
export const resolveTurnResultUrls = ({
  turn,
  isLastTurn,
  isSuccess,
  responseImages = [],
}) => {
  if (turn?.resultUrls && Object.keys(turn.resultUrls).length > 0) {
    return turn.resultUrls;
  }

  if (isLastTurn && isSuccess && responseImages.length > 0) {
    return resultImagesToResultUrlsMap(responseImages);
  }

  return null;
};

/**
 * Enriches the last completed turn with result URLs from batch images.
 */
export const enrichLastTurnWithResultUrls = ({ turns = [], images = [] }) => {
  if (!Array.isArray(turns) || turns.length === 0 || images.length === 0) {
    return turns;
  }

  const resultUrls = resultImagesToResultUrlsMap(images);
  if (Object.keys(resultUrls).length === 0) {
    return turns;
  }

  const lastIndex = turns.length - 1;
  const lastTurn = turns[lastIndex];

  return [
    ...turns.slice(0, lastIndex),
    {
      ...lastTurn,
      resultUrls: {
        ...(lastTurn.resultUrls || {}),
        ...resultUrls,
      },
    },
  ];
};

/**
 * Builds batch items using the latest edited URL per selection when available.
 */
export const buildBatchItemsForJob = ({ items = [], resultUrls = {} }) =>
  items
    .map((item) => {
      if (!item?.selectionId) return null;

      const url = resultUrls[item.selectionId] || item.url;
      if (!url) return null;

      return {
        selectionId: item.selectionId,
        url,
      };
    })
    .filter(Boolean);

/**
 * Builds follow-up payload turns and items from a completed batch state.
 */
export const createFollowUpBatchPayload = ({
  previousTurns = [],
  lastImages = [],
  items = [],
  newPrompt,
  newAttachmentUrls = [],
}) => {
  const normalizedTurns = normalizeBatchPrompts(previousTurns);
  const resultUrls = resultImagesToResultUrlsMap(lastImages);

  const updatedTurns =
    normalizedTurns.length > 0 && Object.keys(resultUrls).length > 0
      ? enrichLastTurnWithResultUrls({
          turns: normalizedTurns,
          images: lastImages,
        })
      : normalizedTurns;

  const nextTurns = buildBatchPromptsForJob({
    history: updatedTurns,
    prompt: newPrompt,
    attachmentUrls: newAttachmentUrls,
  });

  const updatedItems = buildBatchItemsForJob({
    items,
    resultUrls,
  });

  return {
    items: updatedItems,
    prompts: nextTurns,
  };
};

/**
 * Returns completed turns that have result URLs for checkpoint handling.
 */
export const getCompletedBatchTurns = ({
  turns = [],
  isSuccess = true,
  responseImages = [],
}) =>
  turns
    .map((turn, index) => {
      const resultUrls = resolveTurnResultUrls({
        turn,
        isLastTurn: index === turns.length - 1,
        isSuccess,
        responseImages,
      });

      if (!resultUrls || Object.keys(resultUrls).length === 0) {
        return null;
      }

      return {
        prompt: turn.prompt,
        attachmentUrls: turn.attachmentUrls,
        resultUrls,
      };
    })
    .filter(Boolean);

/**
 * Computes checkpoint state for batch edit from completed turns.
 */
export const getBatchCheckpointState = ({ completedTurns = [] }) => {
  const hasCompletedCheckpointBlock =
    completedTurns.length >= PROMPTS_CHECKPOINT_SIZE;

  if (!hasCompletedCheckpointBlock) {
    return {
      checkpointPromptsHistory: completedTurns,
      shouldResetCheckpoint: false,
      latestResultUrls:
        completedTurns.length > 0
          ? completedTurns[completedTurns.length - 1].resultUrls
          : {},
    };
  }

  const latestResultUrls =
    completedTurns[completedTurns.length - 1]?.resultUrls || {};

  return {
    checkpointPromptsHistory: [],
    shouldResetCheckpoint: true,
    latestResultUrls,
  };
};

/**
 * Builds initial gallery items from payload snapshot.
 */
export const buildBatchItemsFromSnapshot = (items = []) =>
  items
    .map((item) => {
      if (!item?.selectionId) return null;

      const url = item.url || item.sourceItem?.url;
      if (!url) return null;

      return {
        selectionId: item.selectionId,
        url,
        sourceItem: item.sourceItem || { id: item.selectionId, url },
        index: item.index ?? null,
      };
    })
    .filter(Boolean);
